import * as path from "path";

import { FSWatcher, watch } from "chokidar";
import _ from "lodash";
import { pathExists } from "fs-extra";

import { globAsync, isDirectoryExists } from "../FsUtils";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";

import { ObservableValueProducerBase } from "./ChainableValueProducer";

export class DirectoryEntriesProducer extends ObservableValueProducerBase<string[]> {
    private readonly pathToDir: string;
    private readonly masks: string[];
    private lastValue?: string[];
    private watcher?: FSWatcher;

    public constructor(pathToDir: string, masks: string[]) {
        super();
        this.pathToDir = pathToDir;
        this.masks = masks;
    }

    protected async beginListening(): Promise<void> {
        if (this.watcher != undefined) {
            return;
        }
        this.watcher = watch(this.masks, {
            ignoreInitial: true,
            cwd: this.pathToDir,
            ignorePermissionErrors: false,
            usePolling: true,
            depth: 1,
        });
        const handleAnyFsEventDebounced = _.debounce(() => this.handleAnyFsEvent(), 1000);
        this.watcher.on("all", handleAnyFsEventDebounced);
        this.watcher.on("error", handleAnyFsEventDebounced);
    }

    protected async stopListening(): Promise<void> {
        if (this.watcher != undefined) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.watcher.close();
        }
    }

    protected async getCurrentValue(): Promise<string[]> {
        this.lastValue = await this.resolveDirectoryContent();
        return this.lastValue;
    }

    private async resolveDirectoryContent(): Promise<string[]> {
        const result: string[] = [];
        for (const mask of this.masks) {
            const paths = await globAsync(mask, { cwd: this.pathToDir });
            for (const matchedPath of paths) {
                if (await isDirectoryExists(path.join(this.pathToDir, matchedPath))) {
                    if (
                        await pathExists(path.join(this.pathToDir, matchedPath, WellKnownDirectories.FormJsonFileName))
                    ) {
                        result.push(matchedPath);
                    }
                }
            }
        }
        return _.uniq(result);
    }

    private async handleAnyFsEvent(): Promise<void> {
        const nextValue = await this.resolveDirectoryContent();
        if (!_.isEqual(this.lastValue, nextValue)) {
            this.lastValue = nextValue;
            await this.emitUpdate(this.lastValue);
        }
    }
}
